import { useCallback, useEffect, useRef, useState } from 'react';

import { PALETTE_ITEM_LIMIT, searchItems } from '../services/globalSearch';
import { isSnippet } from '../components/search/SearchSnippet';
import { normalizeId } from '../utils/ids';
import { useOnSessionChange } from '../utils/session';
import { searchFailure } from './useCollectionSearch';

/** How long typing rests before the term is asked (the tag search's 250 ms). */
export const ITEM_SEARCH_DELAY = 250;
/** The server's own floor for `q` (`GET /api/search`: 2 characters after trimming). */
export const ITEM_SEARCH_MIN = 2;

/** The term as it is asked: whitespace collapsed and trimmed. */
export const itemSearchText = query => `${query ?? ''}`.replace(/\s+/g, ' ').trim();

const EMPTY = Object.freeze([]);

/**
 * The items bucket's hits, made safe to draw: a hit with no id or no
 * collection cannot be opened and is dropped, a repeated id is dropped (the
 * row keys stay unique), and `snippet` is kept only in the shape SearchSnippet
 * draws (an API from before KPR-490 sends none). Nothing else is filtered:
 * what a caller may see is the server's decision, made before the read.
 */
export function normaliseHits(items) {
	if (!Array.isArray(items)) return EMPTY;
	const seen = new Set();
	const hits = [];
	items.forEach((item) => {
		const id = normalizeId(item?._id ?? item?.id);
		const collectionId = normalizeId(item?.collection_id ?? item?.collectionId);
		if (!id || !collectionId || seen.has(id)) return;
		seen.add(id);
		hits.push({
			...item,
			_id: id,
			collection_id: collectionId,
			card_id: normalizeId(item.card_id ?? item.cardId) || null,
			snippet: isSnippet(item.snippet) ? item.snippet : null,
		});
	});
	return hits;
}

const refusalSentence = refused => (refused && typeof refused.message === 'string' && refused.message.trim()
	? refused.message.trim()
	: 'This search could not be run.');

const IDLE = Object.freeze({ term: null, hits: EMPTY, refusal: null, error: null, loading: false, answered: false });

/**
 * ITEMS BY THEIR WORDS, for the command palette (KPR-1706): global search's
 * items bucket (`GET /api/search?types=items`, services/globalSearch) for
 * `query`, one small page, ranked by the server.
 *
 *   useItemSearch(query, { enabled, limit, delay })
 *     -> { active, hits, loading, answered, error, refusal, retry }
 *
 * - Asked from ITEM_SEARCH_MIN characters, `delay` ms after typing rests.
 * - THE NEWEST QUESTION WINS. Every question takes a ticket and its own
 *   AbortController; an answer for an older term, or one that arrives after
 *   the palette closed (`enabled` false), the term fell under two characters
 *   or the account changed, is dropped, never applied.
 * - While a newer term is asked, the previous hits stay (section 14: a change
 *   of term is a refresh, never a blank); they go when the search stops.
 * - A 400 is a REFUSAL of the term (too many words, too long, too broad): its
 *   sentence is the server's, and asking again would not help. A 200 with
 *   `itemsRefused` (an API answering items beside other buckets) is read the
 *   same way. Anything else (429, 5xx, the network) is an `error`, which
 *   `retry` asks again.
 */
export default function useItemSearch(query, { enabled = true, limit = PALETTE_ITEM_LIMIT, delay = ITEM_SEARCH_DELAY } = {}) {
	const text = itemSearchText(query);
	const active = Boolean(enabled) && text.length >= ITEM_SEARCH_MIN;
	const [state, setState] = useState(IDLE);
	const [attempt, setAttempt] = useState(0);
	const ticket = useRef(0);

	// Another account (or none): what was found was the last one's.
	const session = useOnSessionChange(() => {
		ticket.current += 1;
		setState(IDLE);
	});

	useEffect(() => {
		if (!active) {
			ticket.current += 1;
			setState(prev => (prev === IDLE ? prev : IDLE));
			return undefined;
		}
		ticket.current += 1;
		const mine = ticket.current;
		setState(prev => ({ ...prev, loading: true, error: null, refusal: null }));
		const controller = new AbortController();
		const timer = setTimeout(() => {
			searchItems(text, { limit }, { signal: controller.signal })
				.then((data) => {
					if (ticket.current !== mine) return;
					if (data && data.itemsRefused) {
						setState({ term: text, hits: EMPTY, refusal: refusalSentence(data.itemsRefused), error: null, loading: false, answered: true });
						return;
					}
					setState({ term: text, hits: normaliseHits(data?.items), refusal: null, error: null, loading: false, answered: true });
				})
				.catch((failure) => {
					if (ticket.current !== mine || controller.signal.aborted) return;
					const why = searchFailure(failure);
					if (why.refusal) {
						setState({ term: text, hits: EMPTY, refusal: why.message, error: null, loading: false, answered: true });
						return;
					}
					// The hits of an earlier term stay under the failure (DataState's notice).
					setState(prev => ({ ...prev, loading: false, error: why }));
				});
		}, delay);
		return () => {
			clearTimeout(timer);
			controller.abort();
		};
	}, [active, text, limit, delay, attempt, session]);

	const retry = useCallback(() => setAttempt(n => n + 1), []);

	if (!active) return { active: false, hits: EMPTY, loading: false, answered: false, error: null, refusal: null, retry };
	return {
		active: true,
		hits: state.hits,
		loading: state.loading,
		answered: state.answered,
		error: state.error,
		refusal: state.refusal,
		retry,
	};
}
